import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "../css/Inventory.css";
import Card from "./Card";
import CardMenu from "./CardMenu";
import { useInventory } from "../hooks/fetchInventory";
import sortAscSVG from "./SortAscendingLogo";
import sortDescSVG from "./SortDescendingLogo";
import { useLocation } from "react-router-dom";
import MarketFilter from "./MarketFilter";
import CoinIcon from "./CoinIcon";
import useQuickSell from "../hooks/useQuickSell";
import { useCosmetics } from "../hooks/useCosmetics";
import { useUser } from "../hooks/useUser";
import SteamLoginButton from "./SteamLoginButton";

const Inventory = forwardRef(
  (
    {
      onCardClick,
      fullSize = false,
      allowedPlayers,
      team,
      locked = false,
      userNumber,
    },
    ref
  ) => {
    const [isCardInventory, setIsCardInventory] = useState(true);
    const [isSkinInventory, setIsSkinInventory] = useState(false);
    const [sortBy, setSortBy] = useState();
    const [sortOrder, setSortOrder] = useState("desc");
    const [cachedCards, setCachedCards] = useState([]); // State to store cached cards
    const [originalCards, setOriginalCards] = useState([]); // State to store original unfiltered cards
    const [loading, setLoading] = useState(false);
    const {
      fetchInventoryCards,
      fetchInventoryByUserNumber,
      clearInventoryCache,
    } = useInventory();
    const { isGuest } = useUser();
    const [selectedCard, setSelectedCard] = useState(null);
    const [showCardMenu, setShowCardMenu] = useState(false);
    const cardCache = useRef(new Map()); // Ref to store the cached card data
    const location = useLocation();
    const [selectedBoosts, setSelectedBoosts] = useState([null, null, null]);
    const [isGroupSelecting, setIsGroupSelecting] = useState(false);
    const [groupSelectedCards, setGroupSelectedCards] = useState([]);
    const [totalQuickSellValue, setTotalQuickSellValue] = useState(0);
    const [isQuickSelling, setIsQuickSelling] = useState(false); // State to track quick sell loading
    const [reapplyFilters, setReapplyFilters] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const entriesPerPage = 200;

    const { groupQuickSell, fetchCardFantasyTeamStatus, isGroupQuickSelling } =
      useQuickSell();
    const {
      cosmetics,
      loading: cosmeticsLoading,
      getCosmeticsByType,
      updateSelectedCardBackground,
      updateSelectedCardOutline,
      selectedCardBackground,
      selectedCardOutline,
    } = useCosmetics();

    // Card background state
    const [availableCardBackgrounds, setAvailableCardBackgrounds] = useState(
      []
    );
    // Card outline state
    const [availableCardOutlines, setAvailableCardOutlines] = useState([]);
    const [refreshKey, setRefreshKey] = useState(0); // Key to force re-render when card background changes

    const handleSelectedBoostsChange = (boosts) => {
      setSelectedBoosts(boosts);
    };

    const toggleSortOrder = () => {
      if (isGuest) return; // Prevent sorting if guest user
      setSortOrder((prevOrder) => (prevOrder === "desc" ? "asc" : "desc"));
    };

    const marketFilterRef = useRef(null); // Add a ref for the MarketFilter component

    const refetchInventory = async () => {
      setLoading(true); // Start loading
      try {
        if (isCardInventory) {
          setIsSkinInventory(false);

          clearInventoryCache();

          // Use different fetch function based on whether userNumber is provided
          const data = userNumber
            ? await fetchInventoryByUserNumber(userNumber, true) // skipCache = true for refetch
            : await fetchInventoryCards(null, sortBy, sortOrder);

          let filteredData = data || [];
          if (allowedPlayers) {
            filteredData = filteredData.filter((card) =>
              allowedPlayers.some((player) => player._id === card.player._id)
            );
          }
          // Filter out cards that are already in the team
          if (team) {
            filteredData = filteredData.filter(
              (card) => !team.some((teamCard) => teamCard.card._id === card._id)
            );
          }

          // Clear card cache to force re-render
          cardCache.current.clear();

          setCachedCards(filteredData); // Cache the data
          setOriginalCards(filteredData); // Store the original unfiltered data
        }
      } catch (error) {
        console.error("Error fetching inventory");
      } finally {
        setLoading(false); // End loading
        setReapplyFilters(true); // Set reapply filters to true
      }
    };

    useImperativeHandle(ref, () => ({
      refetchInventory,
    }));

    useEffect(() => {
      const fetchData = async () => {
        setLoading(true); // Start loading
        try {
          if (isCardInventory) {
            setIsSkinInventory(false);
            // Use different fetch function based on whether userNumber is provided
            const data = userNumber
              ? await fetchInventoryByUserNumber(userNumber)
              : await fetchInventoryCards(null, sortBy, sortOrder);

            let filteredData = data || [];
            if (allowedPlayers) {
              filteredData = filteredData.filter((card) =>
                allowedPlayers.some((player) => player._id === card.player._id)
              );
            }
            // Filter out cards that are already in the team
            if (team) {
              filteredData = filteredData.filter(
                (card) =>
                  !team.some((teamCard) => teamCard.card._id === card._id)
              );
            }
            setCachedCards(filteredData); // Cache the data
            setOriginalCards(filteredData); // Store the original unfiltered data
          } else if (isSkinInventory) {
            setIsCardInventory(false);
            // Logic to fetch skins and update state accordingly
            // Example:
            // const data = await fetchInventorySkins(null, sortBy, sortOrder);
            // setSkins(data);
          }
        } catch (error) {
          console.error("Error fetching inventory");
        } finally {
          setLoading(false); // End loading
          setReapplyFilters(true); // Set reapply filters to true
        }
      };

      fetchData();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      isCardInventory,
      isSkinInventory,
      allowedPlayers,
      team,
      userNumber,
      location.state?.refresh,
    ]);

    useEffect(() => {
      // Load and apply the saved sort option on page load
      const savedSortOption = localStorage.getItem("sortOption");
      if (savedSortOption) {
        setSortBy(savedSortOption);
        const button = document.getElementById("dropdown-button");
        if (button) {
          button.textContent = `Sort by: ${
            savedSortOption.charAt(0).toUpperCase() + savedSortOption.slice(1)
          }`;
        }
      }
    }, []);

    useEffect(() => {
      // Add the selected card's quicksell value to the total quicksell value
      if (groupSelectedCards.length > 0) {
        let totalValue = 0;
        groupSelectedCards.forEach((card) => {
          totalValue += card.tier * card.stats[0].firepower;
        });
        setTotalQuickSellValue(totalValue);
      } else {
        setTotalQuickSellValue(0);
      }
    }, [groupSelectedCards]);

    useEffect(() => {
      if (!loading && reapplyFilters) {
        setReapplyFilters(false); // Reset reapply filters state
        // Call the handleSubmitFilters function from MarketFilter
        if (marketFilterRef.current) {
          marketFilterRef.current.handleSubmitFilters();
        }
      }
    }, [loading, reapplyFilters]);

    // Effect to update available card backgrounds and outlines
    useEffect(() => {
      setAvailableCardBackgrounds(getCosmeticsByType("cardBackground"));
      setAvailableCardOutlines(getCosmeticsByType("cardOutline"));

      const cardBackgroundButton = document.getElementById(
        "inventory-card-background-dropdown-button"
      );
      if (cardBackgroundButton && selectedCardBackground) {
        cardBackgroundButton.textContent = `Card Background: ${selectedCardBackground.name}`;
      }

      const cardOutlineButton = document.getElementById(
        "inventory-card-outline-dropdown-button"
      );
      if (cardOutlineButton) {
        if (selectedCardOutline && selectedCardOutline.name) {
          cardOutlineButton.textContent = `Card Outline: ${selectedCardOutline.name}`;
        } else {
          cardOutlineButton.textContent = "Card Outline: None";
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cosmetics, selectedCardBackground, selectedCardOutline]);

    const updateCardBackground = async (cardBackgroundId) => {
      try {
        // Wait for the card background to be updated in the backend first
        await updateSelectedCardBackground(cardBackgroundId);

        // Clear cache and refetch inventory
        clearInventoryCache();
        await refetchInventory();

        // Then increment refresh key to force re-render of components
        setRefreshKey((prev) => prev + 1);
      } catch (error) {
        console.error("Error updating card background");
      }
    };

    const updateCardOutline = async (cardOutlineId) => {
      try {
        // Wait for the card outline to be updated in the backend first
        await updateSelectedCardOutline(cardOutlineId);

        // Clear cache and refetch inventory
        clearInventoryCache();
        await refetchInventory();

        // Then increment refresh key to force re-render of components
        setRefreshKey((prev) => prev + 1);
      } catch (error) {
        console.error("Error updating card outline");
      }
    };

    const debounce = (func, delay) => {
      let debounceTimer;
      return function (...args) {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => func.apply(this, args), delay);
      };
    };

    const setSortOption = debounce((option) => {
      // Save the selected option to localStorage
      localStorage.setItem("sortOption", option);

      // Update the dropdown button text
      const button = document.getElementById("dropdown-button");
      if (button) {
        button.textContent = `Sort by: ${
          option.charAt(0).toUpperCase() + option.slice(1)
        }`;
      }

      // Update the state
      setSortBy(option);
    }, 300);

    const sortedCards = useMemo(() => {
      if (!Array.isArray(cachedCards)) return [];
      const sorted = [...cachedCards];

      const compareBoosts = (a, b, boostIndex) => {
        if (selectedBoosts[boostIndex]) {
          const boostA = a.boosts.find(
            (boost) => boost.boost === selectedBoosts[boostIndex]._id
          );
          const boostB = b.boosts.find(
            (boost) => boost.boost === selectedBoosts[boostIndex]._id
          );
          if (boostA && boostB) {
            return sortOrder === "asc"
              ? boostA.value - boostB.value
              : boostB.value - boostA.value;
          } else if (boostA) {
            return -1;
          } else if (boostB) {
            return 1;
          }
        }
        return 0;
      };

      sorted.sort((a, b) => {
        let primaryComparison = 0;
        if (sortBy === "name") {
          primaryComparison =
            sortOrder === "asc"
              ? a.player.name.localeCompare(b.player.name)
              : b.player.name.localeCompare(a.player.name);
        } else if (sortBy === "rarity") {
          primaryComparison =
            sortOrder === "asc" ? a.tier - b.tier : b.tier - a.tier;
        } else if (sortBy === "acquired") {
          primaryComparison =
            sortOrder === "asc"
              ? new Date(a.acquired) - new Date(b.acquired)
              : new Date(b.acquired) - new Date(a.acquired);
        } else if (sortBy === "nationality") {
          primaryComparison =
            sortOrder === "asc"
              ? a.player.country.name.localeCompare(b.player.country.name)
              : b.player.country.name.localeCompare(a.player.country.name);
        } else if (sortBy === "team") {
          primaryComparison =
            sortOrder === "asc"
              ? a.player.team.name.localeCompare(b.player.team.name)
              : b.player.team.name.localeCompare(a.player.team.name);
        } else if (sortBy === "rating") {
          primaryComparison =
            sortOrder === "asc"
              ? a.player.stats[0].firepower - b.player.stats[0].firepower
              : b.player.stats[0].firepower - a.player.stats[0].firepower;
        } else if (sortBy === "boost 1") {
          primaryComparison = compareBoosts(a, b, 0);
        } else if (sortBy === "boost 2") {
          primaryComparison = compareBoosts(a, b, 1);
        } else if (sortBy === "boost 3") {
          primaryComparison = compareBoosts(a, b, 2);
        } else {
          primaryComparison =
            sortOrder === "asc"
              ? a.player.stats[0][sortBy] - b.player.stats[0][sortBy]
              : b.player.stats[0][sortBy] - a.player.stats[0][sortBy];
        }

        // Secondary comparison by name
        if (primaryComparison === 0) {
          return a.player.name.localeCompare(b.player.name);
        }

        return primaryComparison;
      });
      return sorted;
    }, [cachedCards, sortBy, sortOrder, selectedBoosts]);

    // Calculate the current entries to display
    const indexOfLastEntry = currentPage * entriesPerPage;
    const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
    const currentEntries = sortedCards.slice(
      indexOfFirstEntry,
      indexOfLastEntry
    );

    // Calculate total pages
    const totalPages = Math.ceil(sortedCards.length / entriesPerPage);

    const openCardMenu = (card) => {
      setSelectedCard(card);
      setShowCardMenu(true);
    };

    const closeCardMenu = () => {
      setSelectedCard(null);
      setShowCardMenu(false);
    };

    const updateInventoryCache = (cardIDs) => {
      if (Array.isArray(cardIDs)) {
        // Handle array of card IDs
        setCachedCards((prevCards) =>
          prevCards.filter((card) => !cardIDs.includes(card._id))
        );
        setOriginalCards((prevCards) =>
          prevCards.filter((card) => !cardIDs.includes(card._id))
        );
        cardIDs.forEach((cardID) => cardCache.current.delete(cardID)); // Remove each card from cache
      } else {
        // Handle single card ID
        setCachedCards((prevCards) =>
          prevCards.filter((card) => card._id !== cardIDs)
        );
        setOriginalCards((prevCards) =>
          prevCards.filter((card) => card._id !== cardIDs)
        );
        cardCache.current.delete(cardIDs); // Remove from cache
      }
    };

    const resetFilters = () => {
      setCachedCards(originalCards);
    };

    const groupQuicksell = () => {
      setIsGroupSelecting(!isGroupSelecting);
      setGroupSelectedCards([]);
    };

    const addToGroupSelectedCards = async (card) => {
      if (groupSelectedCards.includes(card)) {
        setGroupSelectedCards(
          groupSelectedCards.filter((selectedCard) => selectedCard !== card)
        );
        return;
      }

      // Check card's fantasy team status before adding to groupSelectedCards
      const status = await fetchCardFantasyTeamStatus(card._id);
      if (status.status === "ACTIVE") {
        return;
      }

      setGroupSelectedCards([...groupSelectedCards, card]);
    };

    const handleCardClick = (card) => {
      if (onCardClick) {
        onCardClick(card);
        return;
      } else if (isGroupSelecting && !userNumber) {
        // Only allow group selection for own inventory
        addToGroupSelectedCards(card);
        return;
      } else if (!userNumber) {
        // Only show card menu for own inventory
        openCardMenu(card);
      }
      // Do nothing when viewing another user's inventory (userNumber is provided)
    };

    const resetGroupSelectButton = useRef(() => {
      setIsGroupSelecting(false);
    });

    const handleGroupQuicksell = async () => {
      if (isGroupQuickSelling) {
        return; // Prevent multiple calls
      }

      setIsQuickSelling(true); // Start loading
      const result = await groupQuickSell(
        groupSelectedCards.map((card) => card._id)
      );
      if (result) {
        setGroupSelectedCards([]);
        updateInventoryCache(groupSelectedCards.map((card) => card._id));
        setIsGroupSelecting(false); // Disable group selection
        resetGroupSelectButton.current(); // Reset button state in MarketFilter
      }
      setIsQuickSelling(false); // End loading
    };

    // Reset to first page when filters change
    useEffect(() => {
      setCurrentPage(1);
    }, [cachedCards, sortBy, sortOrder, selectedBoosts]);

    return (
      <div
        className={`main-container ${fullSize ? "" : "col-10 offset-1"}`}
        style={fullSize ? { height: "auto", minHeight: "80%" } : {}}
      >
        <div className="title-container" style={userNumber && { top: "0.5vw" }}>
          <div className=" inventory-box">
            <h1 className="title">Inventory</h1>
          </div>
          <div className="inventory-box-filter">
            {!userNumber && !fullSize && (
              <>
                <div className={`dropdown ${isGuest ? "disabled" : ""}`}>
                  <button
                    id="inventory-card-background-dropdown-button"
                    className="dropdown-button"
                    style={{ width: "17vw" }}
                  >
                    Select Card Background
                  </button>
                  <div id="dropdown-content" className="dropdown-content">
                    {cosmeticsLoading ? (
                      <div className="loader-small" />
                    ) : availableCardBackgrounds &&
                      availableCardBackgrounds.length === 0 ? (
                      <p>No card backgrounds available</p>
                    ) : (
                      availableCardBackgrounds.map((background) => (
                        <p
                          key={background._id}
                          onClick={() => updateCardBackground(background._id)}
                        >
                          {background.name}
                        </p>
                      ))
                    )}
                  </div>
                </div>
                <div className={`dropdown ${isGuest ? "disabled" : ""}`}>
                  <button
                    id="inventory-card-outline-dropdown-button"
                    className="dropdown-button"
                  >
                    Select Card Outline
                  </button>
                  <div id="dropdown-content" className="dropdown-content">
                    {!cosmeticsLoading && (
                      <p
                        key="reset-outline"
                        onClick={() => updateCardOutline(null)}
                      >
                        None
                      </p>
                    )}
                    {cosmeticsLoading ? (
                      <div className="loader-small" />
                    ) : (
                      availableCardOutlines &&
                      availableCardOutlines.length !== 0 &&
                      availableCardOutlines.map((outline) => (
                        <p
                          key={outline._id}
                          onClick={() => updateCardOutline(outline._id)}
                        >
                          {outline.name}
                        </p>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}
            <div className={`dropdown ${isGuest ? "disabled" : ""}`}>
              <button id="dropdown-button" className="dropdown-button">
                Select Sort Option
              </button>
              <div id="dropdown-content" className="dropdown-content">
                <p onClick={() => setSortOption("acquired")}>Acquired</p>
                <p onClick={() => setSortOption("rarity")}>Rarity</p>
                <p onClick={() => setSortOption("rating")}>Rating</p>
                <p onClick={() => setSortOption("name")}>Name</p>
                <p onClick={() => setSortOption("team")}>Team</p>
                <p onClick={() => setSortOption("nationality")}>Nationality</p>
                <p onClick={() => setSortOption("entrying")}>Entrying</p>
                <p onClick={() => setSortOption("opening")}>Opening</p>
                <p onClick={() => setSortOption("sniping")}>Awping</p>
                <p onClick={() => setSortOption("trading")}>Trading</p>
                <p onClick={() => setSortOption("clutching")}>Clutching</p>
                <p onClick={() => setSortOption("utility")}>Utility</p>
                {selectedBoosts[0] && (
                  <p onClick={() => setSortOption("boost 1")}>Boost 1</p>
                )}
                {selectedBoosts[1] && (
                  <p onClick={() => setSortOption("boost 2")}>Boost 2</p>
                )}
                {selectedBoosts[2] && (
                  <p onClick={() => setSortOption("boost 3")}>Boost 3</p>
                )}
              </div>
            </div>
            <div className="sort-container">
              <div
                className="sort-svg"
                onClick={toggleSortOrder}
                dangerouslySetInnerHTML={{
                  __html: sortOrder === "desc" ? sortDescSVG : sortAscSVG,
                }}
              />
            </div>
          </div>
        </div>

        {isCardInventory && (
          <div
            className={`${
              fullSize ? "inventory-main-container" : "market-container"
            }`}
          >
            {isGuest && <SteamLoginButton overlay={true} />}
            <MarketFilter
              ref={marketFilterRef} // Add the ref to the MarketFilter component
              cachedCards={cachedCards}
              setFilteredCards={setCachedCards}
              resetFilters={resetFilters}
              originalCards={originalCards}
              onSelectedBoostsChange={handleSelectedBoostsChange}
              groupQuicksell={groupQuicksell}
              setIsGroupSelecting={setIsGroupSelecting}
              allowGroupSelect={!fullSize && !userNumber} // Disable group select when viewing another user's inventory
              resetGroupSelectButton={resetGroupSelectButton}
              isProfileInventory={!!userNumber} // Check if viewing another user's inventory
            />
            <div
              className={`${
                fullSize
                  ? "inventory-container-fullSize"
                  : "inventory-container"
              }`}
            >
              {locked && (
                <div className="inventory-locked">
                  <p className="inventory-locked-text">Inventory is locked</p>
                </div>
              )}
              <div className="inventory-content">
                {loading && (
                  <div className="loader-overlay">
                    <div className="loader"></div>
                  </div>
                )}
                {currentEntries.length > 0 &&
                  currentEntries.map((card, index) => {
                    // Check if the Card component is already cached with current refresh key
                    const cacheKey = `${card._id}-${refreshKey}`;
                    if (!cardCache.current.has(cacheKey)) {
                      // Cache the Card component without the onClick handler
                      cardCache.current.set(
                        cacheKey,
                        <div className="inventory-card">
                          <Card key={`${index}-${refreshKey}`} card={card} />
                        </div>
                      );
                    }
                    // Wrap the cached Card component with a container that has the latest onClick handler
                    return (
                      <div
                        key={`${index}-${refreshKey}`}
                        className={`col-3 cardDisplay-container ${
                          groupSelectedCards.includes(card)
                            ? "highlighted-card"
                            : ""
                        }`}
                        onClick={() => handleCardClick(card)}
                      >
                        {cardCache.current.get(cacheKey)}
                      </div>
                    );
                  })}
                {isGroupSelecting && (
                  <div className="inventory-sell-container">
                    <div className="inventory-sellbar-container">
                      <CoinIcon width="2vh" height="2vh" />
                      <div className="inventory-sellbar-text">
                        {totalQuickSellValue}
                      </div>
                    </div>
                    <div className="inventory-button-container">
                      {isQuickSelling || isGroupQuickSelling ? (
                        <div className="loader-small"></div>
                      ) : (
                        <button
                          className="default-button"
                          onClick={() => handleGroupQuicksell()}
                          disabled={isGroupQuickSelling}
                        >
                          Quick Sell
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    className="default-button"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1 || totalPages === 0}
                  >
                    Previous
                  </button>
                  <span>
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    className="default-button"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages || totalPages === 0}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {showCardMenu && selectedCard && (
          <div className="overlay" onClick={closeCardMenu}>
            <div onClick={(e) => e.stopPropagation()}>
              <CardMenu
                card={selectedCard}
                onClose={closeCardMenu}
                updateInventoryCache={updateInventoryCache}
              />
            </div>
          </div>
        )}
      </div>
    );
  }
);

export default Inventory;
